import React, { useState, useCallback } from 'react';
import Icon from '../../../components/AppIcon';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

const TEST_ACCOUNTS = [
  {
    id: 'admin-test',
    email: 'admin@test.omramanager.com',
    role: 'super_admin',
    labelFr: '👑 Admin',
    labelAr: '👑 المسؤول',
  },
  {
    id: 'agency-test',
    email: 'agency@test.omramanager.com',
    role: 'agency_admin',
    labelFr: '🏢 Agence',
    labelAr: '🏢 الوكالة',
  },
];

const CHECK_STATUS = {
  IDLE: 'idle',
  CHECKING: 'checking',
  PASS: 'pass',
  FAIL: 'fail',
};

async function checkAccountExists(email) {
  if (!supabaseUrl || !supabaseAnonKey) {
    return { inUsers: false, inIdentities: false, error: 'Supabase not configured' };
  }

  const headers = {
    apikey: supabaseAnonKey,
    Authorization: `Bearer ${supabaseAnonKey}`,
    'Content-Type': 'application/json',
  };

  let inUsers = false;
  let inIdentities = false;
  let usersError = null;
  let identitiesError = null;

  // Check auth.users via admin REST endpoint (uses anon key — will get 401 if RLS blocks, but we check the response shape)
  // We use the PostgREST endpoint for auth schema
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller?.abort(), 8000);
    const res = await fetch(
      `${supabaseUrl}/rest/v1/rpc/check_test_account_exists`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ p_email: email }),
        signal: controller?.signal,
      }
    );
    clearTimeout(timeout);

    if (res?.status === 200) {
      const data = await res?.json();
      inUsers = data?.in_users === true;
      inIdentities = data?.in_identities === true;
    } else if (res?.status === 404 || res?.status === 400) {
      // RPC not found — fall back to direct table query
      usersError = `RPC unavailable (${res?.status}), using fallback`;
    } else {
      usersError = `HTTP ${res?.status}`;
    }
  } catch (err) {
    usersError = err?.name === 'AbortError' ? 'Timeout' : err?.message;
  }

  // Fallback: query user_profiles table (publicly accessible via RLS for service role check)
  if (!inUsers && !inIdentities) {
    try {
      const controller2 = new AbortController();
      const timeout2 = setTimeout(() => controller2?.abort(), 8000);
      const res2 = await fetch(
        `${supabaseUrl}/rest/v1/user_profiles?email=eq.${encodeURIComponent(email)}&select=id,email`,
        {
          method: 'GET',
          headers,
          signal: controller2?.signal,
        }
      );
      clearTimeout(timeout2);

      if (res2?.status === 200) {
        const rows = await res2?.json();
        if (Array.isArray(rows) && rows?.length > 0) {
          inUsers = true;
          // If profile exists, user was created — identity likely exists too
          inIdentities = true;
        } else if (rows?.length === 0) {
          inUsers = false;
          inIdentities = false;
          usersError = null;
          identitiesError = null;
        }
      } else if (res2?.status === 401 || res2?.status === 403) {
        // RLS blocked — try auth endpoint
        usersError = `Profile table blocked (${res2?.status})`;
      } else {
        usersError = `Profile query HTTP ${res2?.status}`;
      }
    } catch (err2) {
      usersError = err2?.name === 'AbortError' ? 'Timeout' : err2?.message;
    }
  }

  // Last resort: try to sign in with a known-bad password to detect if user exists
  // Supabase returns "Invalid login credentials" if user exists but password wrong
  // vs "User not found" if user doesn't exist
  if (!inUsers) {
    try {
      const controller3 = new AbortController();
      const timeout3 = setTimeout(() => controller3?.abort(), 8000);
      const res3 = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          apikey: supabaseAnonKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password: '__health_check_probe__' }),
        signal: controller3?.signal,
      });
      clearTimeout(timeout3);

      if (res3?.status === 400) {
        const body = await res3?.json();
        const msg = (body?.error_description || body?.msg || body?.message || '')?.toLowerCase();
        if (
          msg?.includes('invalid login') ||
          msg?.includes('invalid credentials') ||
          msg?.includes('email not confirmed') ||
          msg?.includes('wrong password') ||
          msg?.includes('password')
        ) {
          // User exists but password wrong — that's a PASS for existence
          inUsers = true;
          inIdentities = true;
          usersError = null;
          identitiesError = null;
        } else if (
          msg?.includes('user not found') ||
          msg?.includes('no user') ||
          msg?.includes('not found')
        ) {
          inUsers = false;
          inIdentities = false;
          usersError = 'User not found in auth.users';
          identitiesError = 'No identity record';
        } else {
          // Unknown 400 — treat as user might exist
          usersError = `Auth probe: ${body?.error_description || body?.msg || 'unknown'}`;
        }
      } else if (res3?.status === 200) {
        // Somehow logged in with probe password — user exists
        inUsers = true;
        inIdentities = true;
      }
    } catch (err3) {
      // Ignore probe errors
    }
  }

  return {
    inUsers,
    inIdentities,
    error: usersError || identitiesError || null,
  };
}

const StatusBadge = ({ status, labelPass, labelFail, labelChecking, labelIdle }) => {
  if (status === CHECK_STATUS?.CHECKING) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
        <Icon name="Loader" size={10} className="animate-spin" />
        {labelChecking}
      </span>
    );
  }
  if (status === CHECK_STATUS?.PASS) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
        <Icon name="CheckCircle" size={10} />
        {labelPass}
      </span>
    );
  }
  if (status === CHECK_STATUS?.FAIL) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">
        <Icon name="XCircle" size={10} />
        {labelFail}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-medium">
      <span className="w-2 h-2 rounded-full bg-gray-300 inline-block" />
      {labelIdle}
    </span>
  );
};

const TestAccountsHealthCheck = ({ currentLanguage = 'fr' }) => {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState({});
  const [lastChecked, setLastChecked] = useState(null);

  const labels = {
    fr: {
      title: 'Health Check — Comptes de test',
      run: 'Vérifier',
      running: 'Vérification…',
      rerun: 'Relancer',
      pass: 'OK',
      fail: 'ABSENT',
      checking: '…',
      idle: '—',
      inUsers: 'auth.users',
      inIdentities: 'auth.identities',
      lastChecked: 'Vérifié à',
      allPass: 'Les deux comptes de test sont présents et opérationnels.',
      someFail: 'Un ou plusieurs comptes de test sont manquants. Exécutez la migration fix_test_accounts.',
      errorHint: 'Détail',
    },
    ar: {
      title: 'فحص الصحة — حسابات الاختبار',
      run: 'فحص',
      running: 'جارٍ الفحص…',
      rerun: 'إعادة الفحص',
      pass: 'موجود',
      fail: 'غائب',
      checking: '…',
      idle: '—',
      inUsers: 'auth.users',
      inIdentities: 'auth.identities',
      lastChecked: 'آخر فحص',
      allPass: 'كلا حسابَي الاختبار موجودان وجاهزان.',
      someFail: 'حساب اختبار واحد أو أكثر مفقود. شغّل migration fix_test_accounts.',
      errorHint: 'تفاصيل',
    },
  };
  const l = labels?.[currentLanguage] || labels?.fr;

  const runHealthCheck = useCallback(async () => {
    if (running) return;
    setRunning(true);
    setResults({});

    // Set all to checking
    const initialState = {};
    TEST_ACCOUNTS?.forEach((acc) => {
      initialState[acc?.id] = { status: CHECK_STATUS?.CHECKING, inUsers: null, inIdentities: null, error: null };
    });
    setResults(initialState);

    // Run checks in parallel
    const checks = await Promise.all(
      TEST_ACCOUNTS?.map(async (acc) => {
        const result = await checkAccountExists(acc?.email);
        return { id: acc?.id, ...result };
      })
    );

    const finalState = {};
    checks?.forEach((check) => {
      const passed = check?.inUsers && check?.inIdentities;
      finalState[check?.id] = {
        status: passed ? CHECK_STATUS?.PASS : CHECK_STATUS?.FAIL,
        inUsers: check?.inUsers,
        inIdentities: check?.inIdentities,
        error: check?.error,
      };
    });

    setResults(finalState);
    setLastChecked(new Date());
    setRunning(false);
  }, [running]);

  const hasResults = Object.keys(results)?.length > 0;
  const allPass = hasResults && Object.values(results)?.every((r) => r?.status === CHECK_STATUS?.PASS);
  const anyFail = hasResults && Object.values(results)?.some((r) => r?.status === CHECK_STATUS?.FAIL);
  const anyChecking = Object.values(results)?.some((r) => r?.status === CHECK_STATUS?.CHECKING);

  const overallIcon = anyChecking
    ? 'Loader'
    : allPass
    ? 'CheckCircle'
    : anyFail
    ? 'XCircle' :'Activity';

  const overallColor = anyChecking
    ? 'text-amber-500'
    : allPass
    ? 'text-emerald-600'
    : anyFail
    ? 'text-red-600' :'text-muted-foreground';

  return (
    <div className="mt-3">
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-border bg-muted/40 hover:bg-muted/70 transition-colors text-sm"
      >
        <span className="flex items-center gap-2 font-medium text-muted-foreground">
          <Icon
            name={overallIcon}
            size={14}
            className={`${overallColor} ${anyChecking ? 'animate-spin' : ''}`}
          />
          {l?.title}
        </span>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-muted-foreground ml-auto" />
      </button>

      {open && (
        <div className="mt-2 rounded-lg border border-border bg-card shadow-sm overflow-hidden text-sm">
          {/* Header row with run button */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-xs font-semibold text-foreground uppercase tracking-wide">
              {l?.title}
            </span>
            <button
              type="button"
              onClick={runHealthCheck}
              disabled={running}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors font-medium"
            >
              <Icon name={running ? 'Loader' : hasResults ? 'RefreshCw' : 'Play'} size={11} className={running ? 'animate-spin' : ''} />
              {running ? l?.running : hasResults ? l?.rerun : l?.run}
            </button>
          </div>

          {/* Results table */}
          <div className="px-4 py-3 space-y-3">
            {TEST_ACCOUNTS?.map((acc) => {
              const r = results?.[acc?.id];
              const accountLabel = currentLanguage === 'fr' ? acc?.labelFr : acc?.labelAr;
              return (
                <div key={acc?.id} className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
                  {/* Account header */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-foreground">{accountLabel}</span>
                    <span className="text-xs font-mono text-muted-foreground truncate max-w-[180px]">
                      {acc?.email}
                    </span>
                  </div>

                  {/* Check rows */}
                  <div className="grid grid-cols-2 gap-2">
                    {/* auth.users */}
                    <div className="flex items-center justify-between gap-1 rounded bg-background border border-border px-2 py-1.5">
                      <span className="text-xs text-muted-foreground font-mono">{l?.inUsers}</span>
                      <StatusBadge
                        status={r?.status === CHECK_STATUS?.CHECKING ? CHECK_STATUS?.CHECKING : r?.inUsers ? CHECK_STATUS?.PASS : r?.status === CHECK_STATUS?.FAIL ? CHECK_STATUS?.FAIL : CHECK_STATUS?.IDLE}
                        labelPass={l?.pass}
                        labelFail={l?.fail}
                        labelChecking={l?.checking}
                        labelIdle={l?.idle}
                      />
                    </div>

                    {/* auth.identities */}
                    <div className="flex items-center justify-between gap-1 rounded bg-background border border-border px-2 py-1.5">
                      <span className="text-xs text-muted-foreground font-mono">{l?.inIdentities}</span>
                      <StatusBadge
                        status={r?.status === CHECK_STATUS?.CHECKING ? CHECK_STATUS?.CHECKING : r?.inIdentities ? CHECK_STATUS?.PASS : r?.status === CHECK_STATUS?.FAIL ? CHECK_STATUS?.FAIL : CHECK_STATUS?.IDLE}
                        labelPass={l?.pass}
                        labelFail={l?.fail}
                        labelChecking={l?.checking}
                        labelIdle={l?.idle}
                      />
                    </div>
                  </div>

                  {/* Error detail */}
                  {r?.error && r?.status !== CHECK_STATUS?.CHECKING && (
                    <p className="text-xs text-amber-600 flex items-start gap-1">
                      <Icon name="AlertCircle" size={11} className="mt-0.5 flex-shrink-0" />
                      <span><span className="font-medium">{l?.errorHint}:</span> {r?.error}</span>
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Summary banner */}
          {hasResults && !anyChecking && (
            <div className={`px-4 py-2.5 border-t border-border flex items-start gap-2 text-xs ${allPass ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              <Icon name={allPass ? 'CheckCircle' : 'AlertTriangle'} size={13} className="flex-shrink-0 mt-0.5" />
              <span>{allPass ? l?.allPass : l?.someFail}</span>
            </div>
          )}

          {/* Last checked */}
          {lastChecked && (
            <div className="px-4 py-2 border-t border-border">
              <p className="text-xs text-muted-foreground">
                {l?.lastChecked}: {lastChecked?.toLocaleTimeString()}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TestAccountsHealthCheck;
